import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";

import { Button } from "@material-ui/core";

import InAppResourcesStore from "stores/InAppResourcesStore";
import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";

import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";

import Page from "components/Layout/Page";
import PublishButton from "components/PublishButton";
import ConfirmationDialog from "components/ConfirmationDialog";
import RestoreArchivedButton from "components/RestoreArchivedButton";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import { SNACKBAR_TYPES } from "components/Snackbar";

import CategoryCard from "./CategoryCard";
import CategoryForm from "./CategoryForm";

import ROUTES from "navigation/routes";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { areStatusActiveProgramFiltersEmpty } from "utils/filter";
import { getConfirmArchivationText } from "utils/publish";

import { PROGRAM_PERMISSION_TYPES } from "constants/models";

import {
  CategoryCardWrapper,
  ScrollLoader,
  OverflowLoader,
  PageControl,
  FilterContainer,
} from "./styles";

const HEADER_WITH_TITLE_HEIGHT = "270px";

type Props = GlobalPopupProps & SnackbarContextProps & RouteComponentProps & {};

@observer
class InAppResources extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;

  @computed get categories() {
    return InAppResourcesStore.items;
  }

  @computed get categoriesListIsEmpty() {
    return this.categories.length === 0;
  }

  @computed get isFiltersEmpty() {
    const { filters } = InAppResourcesStore;

    return areStatusActiveProgramFiltersEmpty(filters);
  }

  @computed get isOverflowLoaderShown() {
    return (
      InAppResourcesStore.isCategoryReordering ||
      InAppResourcesStore.isCategoryPublishing ||
      InAppResourcesStore.isCategoryCopying
    );
  }

  componentDidMount() {
    InAppResourcesStore.fetch();
    ActiveProgramDictionary.fetch();
  }

  componentWillUnmount() {
    InAppResourcesStore.resetPage();
  }

  fetchMoreCategories = async () => {
    const { setMessage } = this.props;

    try {
      await InAppResourcesStore.fetchMoreCategories();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      InAppResourcesStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    InAppResourcesStore.fetch();
  };

  onFilterClear = () => {
    InAppResourcesStore.clearFilters();
    InAppResourcesStore.fetch();
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onManageCategory = (initialValues?: any) => {
    const { openPopup } = this.props;

    openPopup({
      key: "ManageCategory",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <CategoryForm
          isLoading={InAppResourcesStore.isCategoryManaging}
          isFormLoading={InAppResourcesStore.isCategoryLoading}
          initialValues={initialValues}
          onSubmit={this.onManageCategorySubmit(close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
        />
      ),
    });
  };

  onManageCategorySubmit = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { id, activePrograms, title, note } = values;

    const payload = {
      id,
      title: title?.trim(),
      note: note?.trim(),
      activePrograms: activePrograms.map(
        ({ id, switchState }: { id: number; switchState: boolean }) => ({
          activeProgramId: id,
          permission: switchState
            ? PROGRAM_PERMISSION_TYPES.PREVIEW
            : PROGRAM_PERMISSION_TYPES.FULL_ACCESS,
        }),
      ),
    };

    try {
      if (!id) {
        await InAppResourcesStore.createCategory(payload);
      } else {
        await InAppResourcesStore.editCategoryInfo(payload);
      }

      this.resetIsPopupDirty();
      close();
      InAppResourcesStore.resetPage();
      InAppResourcesStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onAddCategory = () => {
    this.onManageCategory();
  };

  onEditCategory = (category: any) => () => {
    const id = category.id;

    this.onManageCategory({ id });
  };

  openCategoryDetails = (category: any) => () => {
    const path = generatePath(ROUTES.IN_APP_RESOURCES_CATEGORY_DETAILS, {
      id: category.id,
    });
    this.props.history.push(path);
  };

  onPublish = (category: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = category;

    try {
      await InAppResourcesStore.updatePublishStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onArchive = (category: any) => () => {
    const { openPopup } = this.props;

    const categoryId = category.id;

    const text = getConfirmArchivationText({
      entity: category,
      suffix: "category",
    });

    openPopup({
      key: "ArchiveCategory",
      canCloseByOuterClick: true,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title={"Archive Category"}
          text={text}
          acceptText={"Archive"}
          declineText={"Cancel"}
          isLoading={InAppResourcesStore.isCategoryManaging}
          onDecline={close}
          onAccept={this.archiveCategory(categoryId, close)}
        />
      ),
    });
  };

  archiveCategory = (id: number, close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await InAppResourcesStore.archiveCategory(id);
      close();
      InAppResourcesStore.resetPage();
      InAppResourcesStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onCopyCategory = (category: any) => async () => {
    const { setMessage } = this.props;
    const id = category.id;

    try {
      await InAppResourcesStore.copyCategory(id);
      InAppResourcesStore.resetPage();
      InAppResourcesStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onDragEnd = async (result: DropResult) => {
    const { setMessage } = this.props;

    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    try {
      await InAppResourcesStore.reorderCategories({ startIndex, endIndex });
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Container header={<Header controls={<PublishButton />} />}>
        <Page
          title="Categories"
          controls={
            <>
              <PageControl>
                <RestoreArchivedButton />
              </PageControl>
              <PageControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.onAddCategory}
                >
                  Add category
                </Button>
              </PageControl>
            </>
          }
          isLoading={InAppResourcesStore.isLoading}
        >
          <FilterContainer>
            <FilteringControl
              initValue={InAppResourcesStore.filters}
              onApply={this.onFilterApply}
              onClear={this.onFilterClear}
              statusFiltering
              accessProgramsFiltering
            />
          </FilterContainer>

          {this.isOverflowLoaderShown && <OverflowLoader />}
          {!this.categoriesListIsEmpty ? (
            <InfiniteScroll
              dataLength={this.categories.length}
              hasMore={InAppResourcesStore.hasMoreInfo}
              next={this.fetchMoreCategories}
              height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
              loader={<ScrollLoader />}
            >
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided, snapshot) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {this.categories.map((category, index) => (
                        <Draggable
                          key={category.id}
                          draggableId={category.id.toString()}
                          index={index}
                          isDragDisabled={!this.isFiltersEmpty}
                        >
                          {(provided, snapshot) => (
                            <CategoryCardWrapper
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <CategoryCard
                                title={category.title}
                                note={category.note}
                                publishStatus={category.publishStatus}
                                isDragging={snapshot.isDragging}
                                onEdit={this.onEditCategory(category)}
                                onCopy={this.onCopyCategory(category)}
                                onDetailsClick={this.openCategoryDetails(
                                  category,
                                )}
                                onArchive={this.onArchive(category)}
                                onPublish={this.onPublish(category)}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            </CategoryCardWrapper>
                          )}
                        </Draggable>
                      ))}

                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </InfiniteScroll>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={"No categories added yet."}
            />
          )}
        </Page>
      </Container>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(InAppResources)));
